import { Dispatch, ReactNode, SetStateAction, useCallback, useState } from "react";
import { LngLat } from "mapbox-gl";
import { Tabs as ArkTabs, createListCollection } from "@ark-ui/react";
import classnames from "classnames";

import mapControlOverlayStyles from "@/components/map/map-control-overlay.module.css";
import MapControlOverlayHeader from "@/components/map/map-control-overlay-header";
import Button from "@/components/button/button";
import { Icon } from "@/components/icon/icon";
import Stack from "@/components/stack/stack";
import { useTranslation } from "@/lib/i18n";

import styles from "./timeseries-overlay.module.css";

interface TimeseriesOverlayProps {
  expanded: boolean;
  setExpanded: Dispatch<SetStateAction<boolean>>;
  onClose: () => void;
  headerTitle?: string;
  latLng?: LngLat;
  children: ReactNode;
}

const TimeseriesOverlay = ({
  expanded,
  setExpanded,
  onClose,
  headerTitle,
  latLng,
  children,
}: TimeseriesOverlayProps) => {
  const { t } = useTranslation();
  const tabList = createListCollection({
    items: [
      {
        label: "Graphs",
        value: "graphs",
      },
      {
        label: "Table",
        value: "table",
      },
    ],
  });
  const [tabValue, setTabValue] = useState(tabList.firstValue);

  const handleClose = useCallback(() => {
    onClose();
    setExpanded(false);
  }, [onClose, setExpanded]);

  const title =
    headerTitle ??
    (latLng
      ? t("dashboard.timeseries.selectedSpot", {
          lat: latLng.lat,
          long: latLng.lng,
        })
      : "");

  return (
    <div className={classnames(mapControlOverlayStyles.controlOverlay, styles.overlayRoot)}>
      <ArkTabs.Root value={tabValue} onValueChange={(e) => setTabValue(e.value)}>
        <MapControlOverlayHeader
          leftIcon={
            <Button variant="ghost" onClick={() => setExpanded((prev) => !prev)}>
              <Icon name={expanded ? "arrow-right" : "arrow-left"} size={20} />
            </Button>
          }
          rightIcon={
            <Button variant="ghost" onClick={handleClose}>
              <Icon name="x" size={20} />
            </Button>
          }
        >
          <Stack gap={16} alignItems="center" justifyContent="space-between">
            <strong>{title}</strong>
            <ArkTabs.List className={styles.tabList}>
              {tabList.items.map((item) => (
                <ArkTabs.Trigger
                  key={item.value}
                  value={item.value}
                  className={styles.tabTrigger}
                >
                  <ArkTabs.Context>
                    {(context) =>
                      context.value === item.value ? <Icon name="check" size={16} /> : null
                    }
                  </ArkTabs.Context>
                  {item.label}
                </ArkTabs.Trigger>
              ))}
            </ArkTabs.List>
          </Stack>
        </MapControlOverlayHeader>

        <div
          style={{
            marginTop: 8,
            width: expanded ? "100cqw" : 400,
            transition: "width .2s ease",
            maxWidth: "calc(100cqw - 72px)",
          }}
        >
          {children}
        </div>
      </ArkTabs.Root>
    </div>
  );
};

export default TimeseriesOverlay;
